// FleetInventory: under the board, the range's AI work by every kind of
// thing it reached, a section a kind: the groups and people who ran it, the
// machines it ran on, the agents and models, the kinds of work, and the
// skills, servers and tools it reached for, the busiest first. Each reads
// what the board's search reaches, and only once it comes near the view, so
// the board above loads as it did. Each finds within itself and shows more on
// request; a row clicked drills down to it, as a card on the board does (the
// one the search is narrowed to already widens it back out). Over its rows,
// when its busiest did their work across the range, as the board's timeline
// draws the agents'.
import { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { AgentIcon } from "./Dimension.jsx";
import { Bar, HOT_FILL, LIT_FILL, REST_FILL, Trend, agentName } from "./FlowBoard.jsx";
import { Glass } from "./SearchPalette.jsx";
import { AGENT_HUES, ActivityStrip } from "./WorkspaceAddOverlay.jsx";
import { query as ask, usePagedDimEntries } from "../lib/dataClient.js";
import { dimensionMeta } from "../lib/dimensionDefs.js";
import { NO_DEPARTMENT, quoteValueIfNeeded } from "../lib/queryLang.js";
import { holdsClause, useWorkspaceSearch } from "../lib/workspaceSearch.js";

const NUM = new Intl.NumberFormat();
// Rows a section shows at first, and adds each time more are asked for.
export const SECTION_ROWS = 30;
const q = quoteValueIfNeeded;
const named = (e) => e.label || e.id;

// The kinds, in the order the page goes down: who, on what, with what, at
// what, reaching for what. Each with the dimension it is read as, what one of
// its values is called (`one` of them), the search clause that narrows to it
// (the board's own), what is said under its name, and what the section is. A
// group is a department in the directory, those with none together.
const groupName = (e) => e.label || e.id || NO_DEPARTMENT;
export const KINDS = [
  { id: "groups", label: "Groups", noun: "groups", one: "group", about: "The departments of the people who ran it", dimKey: "groups", name: groupName, clause: (e) => `group:${q(groupName(e))}` },
  { id: "people", label: "People", noun: "people", one: "person", about: "Who ran the work", dimKey: "identities", name: named, clause: (e) => `person:${q(named(e))}`, sub: (e) => dimensionMeta("identities", e.id)?.department || "" },
  { id: "machines", label: "Machines", noun: "machines", one: "machine", about: "Where the work ran", dimKey: "endpoints", name: named, clause: (e) => `machine:${q(named(e))}`, sub: (e) => dimensionMeta("endpoints", e.id)?.registeredUser || "" },
  { id: "agents", label: "Agents", noun: "agents", one: "agent", about: "The agents that ran it", dimKey: "agents", name: (e) => agentName(e.id), clause: (e) => `agent:${q(agentName(e.id))}`, mark: true },
  { id: "models", label: "Models", noun: "models", one: "model", about: "The models it ran on", dimKey: "models", name: named, clause: (e) => `model:${q(named(e))}` },
  { id: "work", label: "Work", noun: "kinds of work", one: "kind of work", about: "What the work was", dimKey: "clusters", name: named, clause: (e) => `topic:${q(named(e))}` },
  { id: "skills", label: "Skills", noun: "skills", one: "skill", about: "The skills it reached for", dimKey: "skills", name: named, clause: (e) => `skill:${q(named(e))}` },
  { id: "servers", label: "MCP", noun: "MCP servers", one: "MCP server", about: "The MCP servers it reached for", dimKey: "mcp_servers", name: named, clause: (e) => `mcp:${q(named(e))}` },
  { id: "tools", label: "Tools", noun: "tools", one: "tool", about: "The tools it called", dimKey: "tools", name: named, clause: (e) => `tool:${q(named(e))}` },
];

// A section's busiest, each its own colour in the strip over its rows; the
// rest of what it has read, together, in the line's tone, so the colours lead.
export const STRIP_TOP = 5;
export const STRIP_REST = "rest";
const NOOP = () => {};

/**
 * When a section's values did their work across the range: its busiest
 * STRIP_TOP stacked a series each, from their rows' sparklines (the
 * range's stretches, in order), and the rest of its rows as one more.
 * Agents keep the board's colours (`hues`). Null with nothing to draw.
 */
export function sectionStrip(kind, rows, hues = null) {
  const width = Math.max(0, ...rows.map((e) => e.sparkline?.series?.length || 0));
  if (!rows.length || !width) return null;
  const top = rows.slice(0, STRIP_TOP);
  const rest = rows.slice(STRIP_TOP);
  const at = (e, i) => Number(e.sparkline?.series?.[i]) || 0;
  // The legend says what each row says it did.
  const series = top.map((e, i) => ({ id: e.id, label: kind.name(e), color: (kind.mark && hues?.get(e.id)) || AGENT_HUES[i % AGENT_HUES.length], count: Number(e.activity) || 0 }));
  if (rest.length) {
    series.push({ id: STRIP_REST, label: `${rest.length} more ${rest.length === 1 ? kind.one : kind.noun}`, color: "var(--line)", count: rest.reduce((sum, e) => sum + (Number(e.activity) || 0), 0), pick: false });
  }
  const buckets = Array.from({ length: width }, (_, i) => {
    const b = new Map(top.map((e) => [e.id, at(e, i)]));
    if (rest.length) b.set(STRIP_REST, rest.reduce((sum, e) => sum + at(e, i), 0));
    return b;
  });
  return { series, buckets };
}

// Whether a section has come near the view (within a screen below it), for
// as long as it is mounted: what has been read stays read.
export function useNear(ref, root) {
  const [near, setNear] = useState(false);
  useEffect(() => {
    if (near) return undefined;
    const el = ref.current;
    if (!el || typeof IntersectionObserver === "undefined") {
      setNear(true);
      return undefined;
    }
    const io = new IntersectionObserver((seen) => { if (seen.some((s) => s.isIntersecting)) setNear(true); }, { root: root || null, rootMargin: "0px 0px 100% 0px" });
    io.observe(el);
    return () => io.disconnect();
  }, [near, ref, root]);
  return near;
}

// How much of the section's busiest a value is, as the finder draws a
// value's share.
function ShareBar({ share }) {
  return (
    <span className="flex h-[4px] w-full overflow-hidden rounded-[1px] bg-[color:var(--line-soft)]" aria-hidden="true">
      <span className="h-full bg-[color:var(--muted)] opacity-60" style={{ width: `${Math.max(3, Math.min(100, share || 0))}%` }} />
    </span>
  );
}

// `held`: the search is narrowed to it already, so a click widens it back out.
// `lit`: its series is pointed at in the strip over the rows; `onPoint(id)`
// hears it pointed at here, for the strip to bring its series forward.
const Row = memo(function Row({ kind, entry, rank, held, lit = false, onDrill, onPoint = NOOP }) {
  const sub = kind.sub ? kind.sub(entry) : "";
  return (
    <button
      type="button"
      onClick={() => onDrill(kind.clause(entry))}
      onMouseEnter={() => onPoint(entry.id)}
      onMouseLeave={() => onPoint(null)}
      onFocus={() => onPoint(entry.id)}
      onBlur={() => onPoint(null)}
      aria-pressed={held}
      className="grid min-w-0 items-center gap-x-[10px] rounded-[2px] px-[8px] py-[5px] text-left transition-colors duration-150 hover:bg-[var(--row-hover)]"
      style={{ gridTemplateColumns: `22px ${kind.mark ? "14px " : ""}minmax(0,2fr) minmax(32px,1fr) 64px 52px`, background: held ? HOT_FILL : lit ? LIT_FILL : undefined, "--row-hover": LIT_FILL }}
      data-inventory-row={entry.id}
      data-lit={lit ? "" : undefined}
    >
      <span className="text-right text-[11px] tabular-nums text-[color:var(--muted-soft)]">{rank}</span>
      {kind.mark ? <AgentIcon name={entry.id} className="h-[12px] w-[12px] text-[color:var(--muted)]" /> : null}
      <span className="grid min-w-0">
        <span className="truncate text-[12px] leading-[16px] text-[color:var(--ink)]">{kind.name(entry)}</span>
        {sub ? <span className="truncate text-[11px] leading-[14px] text-[color:var(--muted)]">{sub}</span> : null}
      </span>
      <ShareBar share={entry.share} />
      <Trend series={entry.sparkline?.series || []} w={64} h={14} />
      <span className="text-right text-[11px] tabular-nums text-[color:var(--muted)]">{NUM.format(entry.activity || 0)}</span>
    </button>
  );
});

// The rows' sparklines, with those read after their rows (a server's: its
// row says `series: null`, lib/mcpServers.js) read once the section has its
// rows, under the section's reach. The reads of one tick share one query.
// Kept per reach: a new search, scope or range reads them again.
function useRowSparklines(kind, rows, scope, range) {
  const reach = JSON.stringify([kind.dimKey, scope.scopePath, scope.steer || "", range]);
  const lazyIds = useMemo(() => rows.filter((e) => e.sparkline?.series === null).map((e) => e.id), [rows]);
  const [read, setRead] = useState({ reach: "", series: new Map() });
  const held = read.reach === reach ? read.series : null;
  useEffect(() => {
    const missing = lazyIds.filter((id) => !held?.has(id));
    if (!missing.length) return undefined;
    let on = true;
    Promise.all(missing.map((id) => ask("getDimEntrySparkline", { dimKey: kind.dimKey, id, parentScopePath: scope.scopePath, range, query: scope.steer || "" }, { cache: true, staleMs: 30_000 })
      .then((d) => [id, Array.isArray(d?.series) ? d.series : []], () => [id, []])))
      .then((pairs) => {
        if (!on) return;
        setRead((cur) => {
          const series = new Map(cur.reach === reach ? cur.series : []);
          for (const [id, s] of pairs) series.set(id, s);
          return { reach, series };
        });
      });
    return () => { on = false; };
  }, [lazyIds, reach]); // eslint-disable-line react-hooks/exhaustive-deps
  return useMemo(() => (lazyIds.length && held
    ? rows.map((e) => (e.sparkline?.series === null && held.has(e.id) ? { ...e, sparkline: { ...e.sparkline, series: held.get(e.id) } } : e))
    : rows), [rows, lazyIds, held]);
}

// One kind's section: when its busiest did their work across the range
// (`bounds`), its busiest values in columns read down, a search within it, and
// more on request.
function Section({ kind, scope, columns, root, hold, onDrill, sectionRef, bounds = null, hues = null, range = null }) {
  const boxRef = useRef(null);
  const near = useNear(boxRef, root);
  const [text, setText] = useState("");
  const [search, setSearch] = useState("");
  useEffect(() => {
    const t = setTimeout(() => setSearch(text.trim()), 300);
    return () => clearTimeout(t);
  }, [text]);
  const read = usePagedDimEntries({ dimKey: kind.dimKey, parentScopePath: scope.scopePath, steer: scope.steer, search, pageSize: SECTION_ROWS, enabled: near && scope.ready && !hold });
  const [query] = useWorkspaceSearch();
  const setRefs = useCallback((el) => {
    boxRef.current = el;
    sectionRef(kind.id, el);
  }, [sectionRef, kind.id]);
  const rows = useRowSparklines(kind, read.entries, scope, range);
  const strip = useMemo(() => (bounds ? sectionStrip(kind, rows, hues) : null), [kind, rows, hues, bounds]);
  // A value pointed at in the strip's legend or in the rows, lit in both.
  const [pointed, setPointed] = useState(null);
  const pick = useCallback((id) => {
    const entry = rows.find((e) => e.id === id);
    if (entry) onDrill(kind.clause(entry));
  }, [rows, onDrill, kind]);
  const heldSeries = useCallback((id) => {
    const entry = rows.find((e) => e.id === id);
    return !!entry && holdsClause(query, kind.clause(entry));
  }, [rows, query, kind]);
  const perColumn = Math.max(1, Math.ceil(rows.length / columns));
  // The rows in columns read down, a hairline between each.
  const columnsOf = useMemo(() => Array.from({ length: columns }, (_, ci) => rows.slice(ci * perColumn, (ci + 1) * perColumn)).filter((c) => c.length), [rows, columns, perColumn]);
  // Placeholders until something is read: out of reach of the view, held
  // behind the board, or reading (from the moment it is asked for).
  const asked = useRef(false);
  if (read.loading) asked.current = true;
  const busy = !rows.length && (!near || hold || !scope.ready || read.initialLoad || !asked.current);
  const stale = read.revalidating || (rows.length > 0 && (hold || !scope.ready));
  return (
    <section ref={setRefs} className="relative" aria-label={kind.label} data-inventory-section={kind.id}>
      {/* Its head on the page's ground, the rows in a box of their own. */}
      <header className="flex min-w-0 items-end gap-[12px] pb-[10px]">
        <span className="grid min-w-0 gap-[2px]">
          <span className="flex min-w-0 items-baseline gap-[8px]">
            <h2 className="m-0 text-[15px] font-medium leading-[20px] text-[color:var(--ink)]">{kind.label}</h2>
            <span className="text-[12px] tabular-nums text-[color:var(--muted)]" data-inventory-total="">{busy ? "" : NUM.format(read.total)}</span>
          </span>
          <span className="truncate text-[12px] leading-[16px] text-[color:var(--muted)]">{`${kind.about}, the most requests first`}</span>
        </span>
        <label className="ml-auto flex h-[28px] w-[240px] min-w-0 shrink items-center gap-[7px] rounded-[2px] border border-[color:var(--line)] px-[8px] text-[color:var(--muted)] transition-colors focus-within:border-[color:var(--line-strong)]">
          <span className="shrink-0 [&_svg]:h-[12px] [&_svg]:w-[12px]"><Glass /></span>
          <input
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder={`Find ${kind.noun}`}
            aria-label={`Find ${kind.noun}`}
            className="h-full min-w-0 flex-1 bg-transparent text-[12px] text-[color:var(--ink)] outline-none placeholder:text-[color:var(--muted-soft)]"
            data-inventory-search=""
          />
        </label>
      </header>
      <div className="rounded-[2px]" style={{ background: REST_FILL }}>
        {busy ? (
          <div className="grid gap-x-[16px] gap-y-[10px] px-[18px] py-[14px]" style={{ gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))` }} aria-busy="true">
            {Array.from({ length: columns * 3 }, (_, i) => <Bar key={i} w={`${46 + ((i * 17) % 40)}%`} />)}
          </div>
        ) : read.error ? (
          <p className="m-0 px-[18px] py-[14px] text-[12px] text-[color:var(--muted)]">{`The ${kind.noun} could not be read.`}</p>
        ) : !rows.length ? (
          <p className="m-0 px-[18px] py-[14px] text-[12px] text-[color:var(--muted)]">{search ? "Nothing here goes by that name." : `No ${kind.noun} in reach.`}</p>
        ) : (
          <>
            {strip ? (
              <div className="border-b border-[color:var(--line-soft)] pb-[8px] transition-opacity" style={{ opacity: stale ? 0.55 : 1 }} data-inventory-strip="">
                <ActivityStrip buckets={strip.buckets} series={strip.series} from={bounds.from} to={bounds.to} picked={null} onPick={NOOP} cap={0.9} lit={pointed} onSeriesPoint={setPointed} onSeriesPick={pick} held={heldSeries} />
              </div>
            ) : null}
            <div className="grid p-[6px] transition-opacity" style={{ gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))`, opacity: stale ? 0.55 : 1 }} data-inventory-rows="">
              {columnsOf.map((list, ci) => (
                <div key={ci} className={`grid content-start ${ci ? "border-l border-[color:var(--line-soft)] pl-[6px]" : ""} ${ci < columnsOf.length - 1 ? "pr-[6px]" : ""}`}>
                  {list.map((entry, i) => <Row key={entry.id} kind={kind} entry={entry} rank={ci * perColumn + i + 1} held={holdsClause(query, kind.clause(entry))} lit={pointed === entry.id} onDrill={onDrill} onPoint={setPointed} />)}
                </div>
              ))}
            </div>
          </>
        )}
        {!busy && read.hasMore ? (
          <div className="flex items-baseline gap-[10px] border-t border-[color:var(--line-soft)] px-[14px] py-[8px] text-[11px]">
            <button type="button" onClick={read.loadMore} disabled={read.loading} className="text-[color:var(--ink)] hover:text-[color:var(--green)] disabled:text-[color:var(--muted)]" data-inventory-more="">
              {read.loading ? "Reading more..." : `Show ${NUM.format(Math.min(SECTION_ROWS, read.total - read.loadedCount))} more`}
            </button>
            <span className="tabular-nums text-[color:var(--muted-soft)]">{`${NUM.format(read.loadedCount)} of ${NUM.format(read.total)}`}</span>
          </div>
        ) : null}
      </div>
    </section>
  );
}

// Every kind's section, one under another. `sectionRef(id, el)` hears where
// each one is, for the page's navigation; `root` is what scrolls them;
// `hold` keeps them from reading while the board above reads, so it comes
// first (each shows what it last read meanwhile). `onDrill(clause)`: a row
// clicked, to narrow the search to it (or widen it back out). `bounds`: the
// range ({ from, to }), which each section's strip runs across, and `range`
// the range as picked, which the rows are read over; `hues`: the agents'
// colours, as the board gives them. `kinds`: the sections there are (the
// page leaves out the groups where the directory has none).
export default function FleetInventory({ kinds = KINDS, scope, width, root, hold = false, onDrill, sectionRef, bounds = null, hues = null, range = null }) {
  const columns = width >= 1180 ? 3 : width >= 720 ? 2 : 1;
  return (
    <div className="grid gap-[40px]" data-fleet-inventory="">
      {kinds.map((kind) => <Section key={kind.id} kind={kind} scope={scope} columns={columns} root={root} hold={hold} onDrill={onDrill} sectionRef={sectionRef} bounds={bounds} hues={hues} range={range} />)}
    </div>
  );
}
